import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { Loader2, X, Cog, Wrench, FileText, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { Badge } from "../ui/badge";
import toast from 'react-hot-toast';

interface PlanMatrix {
    nombre: string;
    descripcion: string;
    rutinas: {
        id: number;
        nombre: string;
        frecuencia_km: number;
        tipo: string;
        alerta_previa_km?: number;
    }[];
    items_matrix: any[];
}

interface VehiclePlanDetailsModalProps {
    open: boolean;
    onClose: () => void;
    vehiculoId: string;
    placa: string;
    marca?: string;
    modelo?: string;
    planNombre?: string | null;
}

export const VehiclePlanDetailsModal: React.FC<VehiclePlanDetailsModalProps> = ({
    open,
    onClose,
    vehiculoId,
    placa,
    marca,
    modelo,
    planNombre
}) => {
    const [planMatrix, setPlanMatrix] = useState<PlanMatrix | null>(null);
    const [loading, setLoading] = useState(true);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const handleExportExcel = async () => {
        try {
            setExportingExcel(true);
            const response = await maintenanceService.exportVehiclePlanDetailsExcel(vehiculoId);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `plan_mantenimiento_${placa}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Excel descargado correctamente");
        } catch (error) {
            console.error('Error exportando Excel:', error);
            toast.error("Error al descargar Excel");
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExportPdf = async () => {
        try {
            setExportingPdf(true);
            const response = await maintenanceService.exportVehiclePlanDetailsPdf(vehiculoId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `plan_mantenimiento_${placa}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("PDF descargado correctamente");
        } catch (error: any) {
            console.error('Error exportando PDF:', error);
            if (error.response && error.response.status === 501) {
                toast.error("La librería PDF no está instalada en el servidor.");
            } else {
                toast.error("Error al descargar PDF");
            }
        } finally {
            setExportingPdf(false);
        }
    };

    const fetchPlanDetails = async () => {
        try {
            setLoading(true);
            // Reutilizamos el endpoint que ya trae la data del plan
            const response = await maintenanceService.getVehicleHistory(vehiculoId);
            if (response.plan) {
                setPlanMatrix(response.plan);
            }
        } catch (error) {
            console.error('Error fetching plan details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && vehiculoId) {
            fetchPlanDetails();
        }
    }, [open, vehiculoId]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200 border border-slate-200">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50 rounded-t-xl">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-200">
                            <Cog className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Detalles del Plan de Mantenimiento</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="bg-slate-200 text-slate-700 hover:bg-slate-300 font-bold">
                                    {placa}
                                </Badge>
                                <span className="text-sm text-slate-500 font-medium">
                                    {marca} {modelo}
                                </span>
                                {planNombre && (
                                    <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                                        {planNombre}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleExportExcel}
                                disabled={loading || exportingExcel}
                                className="h-8 px-3 gap-2 text-slate-600 hover:text-green-700 hover:bg-green-50"
                                title="Exportar a Excel"
                            >
                                {exportingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                                <span className="text-xs font-medium">Excel</span>
                            </Button>
                            <div className="w-px bg-slate-200 my-1"></div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleExportPdf}
                                disabled={loading || exportingPdf}
                                className="h-8 px-3 gap-2 text-slate-600 hover:text-red-700 hover:bg-red-50"
                                title="Exportar a PDF"
                            >
                                {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                <span className="text-xs font-medium">PDF</span>
                            </Button>
                        </div>

                        <div className="w-px h-6 bg-slate-300 mx-1"></div>

                        <button
                            onClick={onClose}
                            className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-all"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
                            <p className="text-slate-600 font-medium">Cargando estructura del plan...</p>
                        </div>
                    ) : !planMatrix ? (
                        <div className="text-center py-20">
                            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-lg text-slate-600 font-medium">No se encontró información del plan asignado.</p>
                        </div>
                    ) : (
                        <div className="space-y-8 pb-10">
                            {/* Plan Info Card */}
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <Wrench className="h-24 w-24 text-blue-900" />
                                </div>
                                <div className="relative z-10">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-3">
                                                {planMatrix.nombre}
                                            </h3>
                                            <p className="text-slate-600 text-sm leading-relaxed max-w-2xl">
                                                {planMatrix.descripcion || 'Sin descripción disponible.'}
                                            </p>
                                        </div>
                                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex flex-col items-end">
                                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Costo Total Referencial</span>
                                            <span className="text-3xl font-bold text-blue-900">
                                                {new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(
                                                    planMatrix.rutinas.reduce((totalPlan, rutina) => {
                                                        const costoRutina = planMatrix.items_matrix.reduce((acc, item) => {
                                                            const cantidad = Number(item[`rutina_${rutina.id}`]) || 0;
                                                            return acc + (cantidad * (Number(item.precio_unitario) || 0));
                                                        }, 0);
                                                        return totalPlan + costoRutina;
                                                    }, 0)
                                                )}
                                            </span>
                                            <span className="text-[10px] text-blue-400 mt-1">* Suma de todas las rutinas</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Plan Matrix Table */}
                            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
                                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                                    <h4 className="font-bold text-slate-700 flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        Items y Rutinas Programadas
                                    </h4>
                                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider bg-white border border-slate-200 px-2 py-1 rounded">
                                        Vista Matricial
                                    </span>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider border-b border-slate-200">
                                                <th className="px-6 py-4 font-bold min-w-[300px] sticky left-0 bg-slate-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                    Item / Repuesto / Servicio
                                                </th>
                                                <th className="px-4 py-4 font-bold text-center w-24 bg-slate-50">Unidad</th>
                                                <th className="px-4 py-4 font-bold text-right w-32 bg-slate-50 border-r border-slate-200">Precio Un.</th>
                                                {planMatrix.rutinas.map(rutina => (
                                                    <th key={rutina.id} className="px-4 py-4 font-bold text-center min-w-[140px] border-r border-slate-200 bg-white">
                                                        <div className="flex flex-col gap-1 items-center">
                                                            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
                                                                {rutina.nombre}
                                                            </Badge>
                                                            <span className="text-[10px] text-slate-500 mt-1">
                                                                cada {rutina.frecuencia_km.toLocaleString()} km
                                                            </span>
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {planMatrix.items_matrix
                                                .sort((a: any, b: any) => a.nombre.localeCompare(b.nombre))
                                                .map((item, index) => (
                                                    <tr key={index} className="hover:bg-slate-50 transition-colors group">
                                                        <td className="px-6 py-3 font-medium text-slate-700 border-r border-slate-100 sticky left-0 bg-white group-hover:bg-slate-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-semibold text-slate-800">{item.nombre}</span>
                                                                <span className="text-[11px] text-slate-400 font-mono mt-0.5">{item.codigo_interno}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-slate-500 text-xs font-semibold">
                                                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-normal">
                                                                {item.unidad_medida}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-slate-600 border-r border-slate-100 font-mono text-xs">
                                                            {item.precio_unitario ? new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(Number(item.precio_unitario)) : '-'}
                                                        </td>
                                                        {planMatrix.rutinas.map(rutina => {
                                                            const cantidad = Number(item[`rutina_${rutina.id}`]);
                                                            return (
                                                                <td key={rutina.id} className="px-4 py-3 text-center border-r border-slate-100">
                                                                    {cantidad ? (
                                                                        <div className="flex flex-col items-center justify-center gap-1">
                                                                            <span className={`
                                                                                inline-flex items-center justify-center font-bold rounded-lg w-8 h-8 transition-all
                                                                                ${cantidad > 1
                                                                                    ? 'bg-blue-100 text-blue-700 border border-blue-200 text-xs shadow-sm'
                                                                                    : 'bg-green-100 text-green-700 border border-green-200 shadow-sm'
                                                                                }
                                                                            `}>
                                                                                {cantidad > 1 ? cantidad : <CheckCircle2 className="w-4 h-4" />}
                                                                            </span>
                                                                            {item.precio_unitario && (
                                                                                <span className="text-[10px] text-slate-400 font-mono">
                                                                                    {new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', maximumFractionDigits: 0 }).format(cantidad * Number(item.precio_unitario))}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-slate-200 text-lg select-none">•</span>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            {planMatrix.items_matrix.length === 0 && (
                                                <tr>
                                                    <td colSpan={3 + planMatrix.rutinas.length} className="px-6 py-12 text-center text-slate-500 italic">
                                                        No hay ítems registrados en este plan.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {/* Table Footer with Totals */}
                                        <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-700 sticky bottom-0 z-20 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                                            <tr>
                                                <td className="px-6 py-4 text-right bg-slate-50 sticky left-0 z-30 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-slate-200" colSpan={3}>
                                                    COSTO REFERENCIAL POR RUTINA
                                                </td>
                                                {planMatrix.rutinas.map(rutina => {
                                                    const totalRutina = planMatrix.items_matrix.reduce((acc, item) => {
                                                        const cantidad = Number(item[`rutina_${rutina.id}`]) || 0;
                                                        return acc + (cantidad * (Number(item.precio_unitario) || 0));
                                                    }, 0);

                                                    return (
                                                        <td key={rutina.id} className="px-4 py-4 text-center border-r border-slate-200 bg-white">
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-sm font-bold text-slate-800">
                                                                    {new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(totalRutina)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50/80 rounded-b-xl flex justify-end">
                    <Button onClick={onClose} size="lg" className="bg-slate-900 text-white hover:bg-slate-800 px-8">
                        Cerrar Detalles
                    </Button>
                </div>
            </div>
        </div>
    );
};
